import { useEffect } from 'react';
import { Alert } from '@billda/design-system';
import { useSnackbarStore } from '../../stores/snackbarStore';

/**
 * Global app snackbar for success/error/info/warning notifications.
 *
 * Trigger from anywhere using snackbarStore:
 * ```ts
 * const { showSnackbar } = useSnackbarStore();
 * showSnackbar('Saved successfully');
 * showSnackbar('Something went wrong', 'error');
 * ```
 *
 * Mount once in App.tsx.
 */
export function AppSnackbar() {
  const { open, message, severity, autoHideDuration, hideSnackbar } =
    useSnackbarStore();

  useEffect(() => {
    if (!open || autoHideDuration === null) {
      return undefined;
    }
    const timer = window.setTimeout(hideSnackbar, autoHideDuration);
    return () => {
      window.clearTimeout(timer);
    };
  }, [open, autoHideDuration, message, hideSnackbar]);

  if (!open) {
    return null;
  }

  return (
    <div
      role="presentation"
      className="pointer-events-none fixed bottom-4 left-1/2 z-50 flex w-[calc(100%-2rem)] max-w-md -translate-x-1/2 justify-center"
    >
      <Alert
        severity={severity}
        variant="filled"
        onClose={hideSnackbar}
        className="pointer-events-auto w-full shadow-lg"
      >
        {message}
      </Alert>
    </div>
  );
}
